import { useState, useRef, useEffect } from 'react';
import isEqual from 'react-fast-compare';
import diff from '../utils/diff';
import ContextControl from '../controllers/ContextControl';
import useIsEqualMemoize from './useIsEqualMemoize';
import getDict from '../utils/getDict';

export default function useContextControl(props, formStateRef) {
  const contextControlStateRef = useRef();
  const [contextControl] = useState(
    () => new ContextControl(props, contextControlStateRef, formStateRef)
  );
  const prevValues = useRef(contextControl.stateRef.current.values);
  const prevErrorsDict = useRef(contextControl.stateRef.current.errorsDict);
  const prevSchemaMap = useRef(new Map(contextControl.stateRef.schemaMap));
  const prevTouched = useRef(new Set(contextControl.stateRef.touched));
  const propsRef = useRef();
  const isMounted = useRef(false);

  propsRef.current = props;

  useState(() => {
    contextControl.subscribe('state', (nextFormState) => {
      const { values, errorsDict, schemaMap, touched, touchedValues } = nextFormState;
      const { schema } = propsRef.current;
      const prevInitialValues = contextControl.stateRef.current.initialValues;
      if (touchedValues.size > 0 && !isEqual(prevValues.current, values)) {
        // Robin I updated the below to fix on change issue. TODO verify this fix is ok
        // const changedValues = diff(getDict(prevValues.current), getDict(values));
        const changedValues = diff(
          getDict({ ...prevInitialValues, ...prevValues.current }),
          getDict(values)
        );
        prevValues.current = values;

        if (Object.keys(changedValues).length > 0) {
          contextControl.requestCompileSchema(schema);

          contextControl.trigger('change', changedValues);
        }
      }

      if (!isEqual(prevErrorsDict.current, errorsDict)) {
        const changedErrors = diff(prevErrorsDict.current, errorsDict);
        prevErrorsDict.current = errorsDict;

        contextControl.trigger('error', changedErrors);
      }

      // TODO: do we still need this event?
      if (!isEqual([...prevSchemaMap.current], [...schemaMap])) {
        const changedMap = diff(prevSchemaMap.current, schemaMap);
        prevSchemaMap.current = schemaMap;

        contextControl.trigger('schema', changedMap);
      }

      // TODO: do we still need this event?
      if (!isEqual(prevTouched.current, touched)) {
        const changedTouched = diff(prevTouched.current, touched);
        prevTouched.current = touched;

        contextControl.trigger('touched', changedTouched);
      }
    });
  });

  useEffect(() => {
    contextControl.setProps(props);
  }, [useIsEqualMemoize(props)]);

  const afterMount = (fn) => {
    if (!isMounted.current) {
      return () => {};
    }

    return fn;
  };

  const { schema, initialValues } = props;

  useEffect(
    afterMount(() => {
      contextControl.compileSchema(schema);
    }),
    [useIsEqualMemoize(schema)]
  );

  useEffect(
    afterMount(() => {
      contextControl.setInitialValues(initialValues);
    }),
    [useIsEqualMemoize(initialValues)]
  );

  // keep track of mounted state
  // IMPORTANT: needs to come after references to isMounted.current
  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  return contextControl;
}
